import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonVariant,
  Card,
  Link,
  Tab,
  Tabs,
  TabsList,
  Text,
  Tooltip,
  SkeletonLoader,
} from "@merge-api/merge-javascript-shared";
import useManageConditionPresets from "./hooks/useManageConditionPresets";
import ConditionPresetsList from "./ConditionPresetsList";
import ConditionPresetConfirmDiscardChangesDialog from "./ConditionPresetConfirmDiscardChangesDialog";
import ConditionPresetHeader from "./ConditionPresetHeader";
import ConditionPresetPublishChangesDialog from "./ConditionPresetPublishChangesDialog";
import ConditionPresetPreviewLinkDialog from "./ConditionPresetPreviewLinkDialog";
import useLoadConditionPresetData from "./hooks/useLoadConditionPresetData";
import useConditionPresetIntegrations from "./ConditionPresetPreviewLinkDialog/hooks/useConditionPresetIntegrations";
import { useHistory, useParams } from "react-router-dom";
import useAppContext from "../../../context/useAppContext";
import { fetchWithAuth } from "../../../../api-client/APIClient";
import { OrganizationCustomization } from "../link/context/CustomizationContext";
import { APICategory, HTTPMethod, UserType } from "../../../../models/Entities";
import { MonitorUp } from "lucide-react";
import { categoryDisplayName, getIntegrationCategoryList } from "../../../../models/Helpers";
import { FULL_SELECTIVE_SYNC_BILLING_PLANS } from "../../../../constants";

interface CategoryProps {
  category: APICategory;
}

const NoFiltersAvailable = ({ category }: CategoryProps) => (
  <div className="my-8 flex justify-center flex-col items-center">
    <Text variant="h4" className="flex text-gray-40">
      {category === APICategory.filestorage || category === APICategory.knowledgebase
        ? `Filters for ${categoryDisplayName(category)} can only be set by your users in Merge Link`
        : `No filters are available for ${categoryDisplayName(category)}`}
    </Text>
    <Text variant="sm" className="flex text-gray-40 font-semibold">
      {category === APICategory.filestorage || category === APICategory.knowledgebase
        ? "You will see applied filters in each Linked Account"
        : `Selective Sync filters for ${categoryDisplayName(
            category,
          )} integrations can only be set by your end users in Merge Link.`}
    </Text>
  </div>
);

const LoadingState = () => (
  <div className="flex flex-col gap-y-6">
    <SkeletonLoader fullWidth height={16} borderRadius={4} className="my-0.5" />
    <SkeletonLoader fullWidth height={48} borderRadius={4} className="my-0.5" />
    <SkeletonLoader fullWidth height={48} borderRadius={4} className="my-0.5" />
    <SkeletonLoader fullWidth height={48} borderRadius={4} className="my-0.5" />
  </div>
);

const NoMappedFiltersMessage = ({ category }: CategoryProps) => (
  <div className="my-8 flex justify-center">
    <Text variant="h4" className="text-gray-40">
      No filters are currently available for {categoryDisplayName(category)}
    </Text>
  </div>
);

const UpsellFiltersMessage = () => (
  <div className="my-8 flex flex-col justify-center items-center">
    <Text variant="h4" className="text-gray-40">
      No filters available
    </Text>
    <Text variant="sm" className="text-gray-40 font-semibold">
      <Link href="/billing" className="text-blue">
        Upgrade your plan{" "}
      </Link>
      to add filters
    </Text>
  </div>
);

const ConfigurationSelectiveSync = () => {
  const { user } = useAppContext();
  const isSupportRole = user.type === UserType.support;
  const [organizationCustomization, setOrganizationCustomization] =
    useState<OrganizationCustomization | null>(null);

  // state
  const [publishChangesDialogOpen, setPublishChangesDialogOpen] = useState(false);
  const [conditionPresetPreviewLinkDialogOpen, setConditionPresetPreviewLinkDialogOpen] =
    useState(false);

  // hooks
  const { category } = useParams<{ category: APICategory }>();
  const { loading, refetch, remoteConditionPresets, conditionPresetMeta } =
    useLoadConditionPresetData({});
  const {
    conditionPresets,
    conditionPresetErrors,
    onConditionPresetChange,
    onConditionPresetDelete,
    onConditionPresetAdd,
    publishChanges,
    changes,
    publishing,
    hasChanges,
    validateChanges,
  } = useManageConditionPresets({
    remoteConditionPresets,
    refetch: () => {
      refetch();
      setPublishChangesDialogOpen(false);
    },
  });
  const { previewLinkEnabled, hasMappedFiltersForCategory } = useConditionPresetIntegrations({
    conditionPresets,
    conditionPresetMeta,
    category,
  });

  useEffect(() => {
    fetchWithAuth({
      path: `integrations/customizable-link-settings`,
      method: "GET",
      onResponse: (customizationSettings) => {
        // if a customer does not have OrganizationCustomization but does have Organization, then we need to POST  and set
        // OrganizationCustomization.show_selective_sync_screen to Organization.show_selective_sync so that further changes
        // to this value can be processed correctly
        if (!customizationSettings.organization_customization) {
          const newOrganizationCustomization: OrganizationCustomization = {
            status: "PUBLISHED",
            show_selective_sync_screen: user.organization.show_selective_sync ?? false,
          };
          fetchWithAuth({
            path: `/integrations/create-update-customizable-link-settings`,
            method: HTTPMethod.POST,
            body: {
              status: "PUBLISHED",
              organization_customization_data: newOrganizationCustomization,
              integration_customizations_data: null,
            },
            onResponse: () => {},
          });
          setOrganizationCustomization(newOrganizationCustomization);
        } else {
          setOrganizationCustomization(customizationSettings.organization_customization);
        }
      },
    });
  }, []);

  // event handlers
  const onPublishChangesClick = () => {
    const hasErrors = validateChanges();

    if (hasErrors) return;
    setPublishChangesDialogOpen(true);
  };

  // this covers the non enterprise customers who were on the s2 flag who should not have a gated experience
  const selectiveSyncEnabled = user.is_selective_sync_enabled;

  const showPickerSyncSelections =
    category === APICategory.filestorage || category === APICategory.knowledgebase;

  const currentTier =
    user.organization.organization_billing_plan?.billing_plan.plan_tier || undefined;
  const showGatedSelectiveSync =
    !selectiveSyncEnabled &&
    !!currentTier &&
    !FULL_SELECTIVE_SYNC_BILLING_PLANS.includes(currentTier);

  const history = useHistory();

  // state
  const [tempCategory, setTempCategory] = useState<APICategory>();

  // event handlers
  const onCategoryChange = (category: APICategory | null) => {
    // set temp category to temporarily decouple Select from the category in url.
    // this is necessary b/c select maintains an internal state and expects when you
    // change, if controlled, that you actually update the external state, which
    // is not always true due to our usage of the react router Prompt
    setTempCategory(category!);
    history.push(`/configuration/selective-sync/${category!}`);
    setTimeout(() => setTempCategory(undefined), 0);
  };

  return (
    <>
      <div className="flex flex-col pb-5">
        <ConditionPresetHeader
          organizationCustomization={organizationCustomization}
          setOrganizationCustomization={setOrganizationCustomization}
          previewLinkEnabled={previewLinkEnabled}
          setConditionPresetPreviewLinkDialogOpen={setConditionPresetPreviewLinkDialogOpen}
          showGatedSelectiveSync={showGatedSelectiveSync}
        />

        <Card>
          <div className="px-6 py-4 flex flex-row items-center justify-between border-b border-gray-10">
            <Text variant="h5">Preset filters</Text>

            <Tooltip title={!hasChanges && "No changes to publish"}>
              <Button
                size="sm"
                variant={ButtonVariant.PrimaryGray}
                fullWidth
                leftIcon={<MonitorUp className="h-3 w-3" />}
                onClick={onPublishChangesClick}
                disabled={!hasChanges}
              >
                Publish changes
              </Button>
            </Tooltip>
          </div>
          <div className="flex flex-col p-6">
            <Tabs
              onChange={(_, value) => onCategoryChange(value as APICategory)}
              value={category}
              className="flex flex-col gap-y-6"
            >
              <TabsList className="w-fit">
                {getIntegrationCategoryList(user)
                  .filter((category) => category !== APICategory.mktg)
                  .map((category) => (
                    <Tab value={category}>{categoryDisplayName(category)}</Tab>
                  ))}
              </TabsList>

              {showPickerSyncSelections ? (
                <NoFiltersAvailable category={category} />
              ) : loading || !conditionPresetMeta ? (
                <LoadingState />
              ) : remoteConditionPresets?.length === 0 &&
                hasMappedFiltersForCategory &&
                showGatedSelectiveSync ? (
                // no default filters, but has mapped filters, but is not enterprise so can't use those filters
                <UpsellFiltersMessage />
              ) : hasMappedFiltersForCategory ? (
                <ConditionPresetsList
                  conditionPresets={conditionPresets}
                  conditionPresetErrors={conditionPresetErrors}
                  conditionPresetMeta={conditionPresetMeta}
                  onConditionPresetChange={onConditionPresetChange}
                  onConditionPresetDelete={onConditionPresetDelete}
                  onConditionPresetAdd={onConditionPresetAdd}
                  showGatedSelectiveSync={showGatedSelectiveSync}
                  organizationCustomization={organizationCustomization}
                  category={category}
                />
              ) : (
                <NoMappedFiltersMessage category={category} />
              )}
            </Tabs>
          </div>
        </Card>
      </div>

      <ConditionPresetConfirmDiscardChangesDialog hasChanges={hasChanges} />

      <ConditionPresetPublishChangesDialog
        changes={changes}
        open={publishChangesDialogOpen}
        onClose={() => setPublishChangesDialogOpen(false)}
        publishChanges={publishChanges}
        publishing={publishing}
      />

      <ConditionPresetPreviewLinkDialog
        conditionPresets={conditionPresets}
        conditionPresetMeta={conditionPresetMeta}
        open={conditionPresetPreviewLinkDialogOpen}
        onClose={() => setConditionPresetPreviewLinkDialogOpen(false)}
        category={category}
      />
    </>
  );
};

export default ConfigurationSelectiveSync;
